import { Button } from "@components/button/button-untitledui";
import { Tool02 } from "@untitledui/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import {
	getPreventiveMaintenanceErrorMessage,
	PREVENTIVE_FIELD_API_TO_FORM,
} from "@/features/vehicle/constants/preventiveMaintenanceErrors";
import {
	useCreatePreventiveMaintenance,
	useGetPreventiveMaintenance,
	useUpdatePreventiveMaintenance,
} from "@/features/vehicle/query";
import type {
	PreventiveMaintenance,
	PreventiveMaintenanceRequest,
} from "@/features/vehicle/types";
import { veiculosPreventiva } from "@/services/analytics/events";
import {
	type PreventiveMaintenanceFormData,
	PreventiveMaintenanceModal,
} from "./preventive-maintenance-modal";

dayjs.extend(customParseFormat);

type PreventiveCardProps = {
	vehicleId: string;
};

function formatKm(value: number): string {
	if (value) {
		return `${value.toLocaleString("pt-BR")} km`;
	}
	return "-";
}

/** Exibe data em dd/MM/yyyy. Aceita string em yyyy-MM-dd (API) ou dd/MM/yyyy. */
function formatDateToDdMmYyyy(value: string): string {
	if (!value?.trim()) {
		return value ?? "";
	}
	const s = value.trim();
	// yyyy-MM-dd → dd/MM/yyyy
	const d = dayjs(s, "YYYY-MM-DD", true);
	if (d.isValid()) {
		return d.format("DD/MM/YYYY");
	}
	// já dd/MM/yyyy
	return s;
}

/** Parse dd/MM/yyyy ou yyyy-MM-dd. Retorna dayjs ou null se inválido. */
function parseDate(value: string): dayjs.Dayjs | null {
	const s = value?.trim();
	if (!s) {
		return null;
	}
	const d = dayjs(s, ["DD/MM/YYYY", "YYYY-MM-DD"], true);
	if (d.isValid()) {
		return d;
	}
	return null;
}

type PreventiveFieldErrors = Partial<
	Record<keyof PreventiveMaintenanceRequest, string>
>;

const REQUIRED_FIELD_MESSAGES: PreventiveFieldErrors = {
	dateOfLastPreventive: "Data da última preventiva é obrigatória.",
	kilometersOfLastPreventive:
		"Quilometragem da última preventiva é obrigatória.",
	dateOfNextPreventive: "Data da próxima preventiva é obrigatória.",
};

function validateRequiredFields(
	data: PreventiveMaintenanceRequest
): PreventiveFieldErrors {
	const errors: PreventiveFieldErrors = {};
	if (!data.dateOfLastPreventive?.trim()) {
		errors.dateOfLastPreventive = REQUIRED_FIELD_MESSAGES.dateOfLastPreventive;
	}
	if (
		data.kilometersOfLastPreventive === 0 ||
		data.kilometersOfLastPreventive === undefined
	) {
		errors.kilometersOfLastPreventive =
			REQUIRED_FIELD_MESSAGES.kilometersOfLastPreventive;
	}
	if (!data.dateOfNextPreventive?.trim()) {
		errors.dateOfNextPreventive = REQUIRED_FIELD_MESSAGES.dateOfNextPreventive;
	}
	return errors;
}

function validateLastDateNotFuture(dateStr: string): string | null {
	const d = parseDate(dateStr);
	if (!d) {
		return null;
	}
	const today = dayjs().startOf("day");
	if (d.isAfter(today)) {
		return "A data da última preventiva não pode ser uma data futura.";
	}
	return null;
}

function validatePreventiveDates(
	data: PreventiveMaintenanceRequest
): PreventiveFieldErrors {
	const lastDate = parseDate(data.dateOfLastPreventive);
	const nextDate = parseDate(data.dateOfNextPreventive);
	const today = dayjs().startOf("day");
	const errors: PreventiveFieldErrors = {};
	// Data da última não pode ser futura
	if (lastDate?.isAfter(today)) {
		errors.dateOfLastPreventive =
			"A data da última preventiva não pode ser uma data futura.";
	}
	if (nextDate && lastDate && !nextDate.isAfter(lastDate)) {
		errors.dateOfNextPreventive =
			"A data da próxima preventiva deve ser posterior à data da última.";
	}
	if (nextDate?.isBefore(today)) {
		errors.dateOfNextPreventive =
			errors?.dateOfNextPreventive ??
			"A data da próxima preventiva não pode ser uma data passada.";
	}
	return errors;
}

type FleeterApiError = {
	detail?: string;
	errors?: string[];
	fieldsErrors?: Record<string, string[]>;
};

function getApiErrorFromException(e: unknown): FleeterApiError | null {
	const responseData = (e as { response?: { data?: unknown } })?.response?.data;
	if (
		!responseData ||
		typeof responseData !== "object" ||
		!("error" in responseData)
	) {
		return null;
	}
	return (responseData as { error: FleeterApiError }).error;
}

function mapApiFieldErrorsToForm(
	fieldsErrors: Record<string, string[]>
): PreventiveFieldErrors {
	const mapped: PreventiveFieldErrors = {};
	for (const [apiKey, messages] of Object.entries(fieldsErrors)) {
		const formKey = PREVENTIVE_FIELD_API_TO_FORM[apiKey];
		if (formKey && messages[0]) {
			mapped[formKey] = messages[0];
		}
	}
	return mapped;
}

export const PreventiveCard = ({ vehicleId }: PreventiveCardProps) => {
	const { data: preventive, isLoading } =
		useGetPreventiveMaintenance(vehicleId);
	const createMutation = useCreatePreventiveMaintenance();
	const updateMutation = useUpdatePreventiveMaintenance();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState<"add" | "edit">("add");
	const [formData, setFormData] = useState<PreventiveMaintenanceFormData>({
		dateOfLastPreventive: "",
		kilometersOfLastPreventive: 0,
		dateOfNextPreventive: "",
	});
	const [alert, setAlert] = useState<{
		type: "success" | "error";
		title: string;
		description?: string;
	} | null>(null);
	const [fieldErrors, setFieldErrors] = useState<
		Partial<Record<keyof PreventiveMaintenanceRequest, string>>
	>({});

	const openAddModal = useCallback(() => {
		setModalMode("add");
		setFormData({
			dateOfLastPreventive: "",
			kilometersOfLastPreventive: 0,
			dateOfNextPreventive: "",
		});
		setAlert(null);
		setFieldErrors({});
		veiculosPreventiva();
		setIsModalOpen(true);
	}, []);

	const openEditModal = useCallback(() => {
		if (!preventive) {
			return;
		}
		setModalMode("edit");
		setFormData({
			dateOfLastPreventive: preventive.dateOfLastPreventive,
			kilometersOfLastPreventive: preventive.kilometersOfLastPreventive,
			dateOfNextPreventive: preventive.dateOfNextPreventive,
		});
		setAlert(null);
		setFieldErrors({});
		veiculosPreventiva();
		setIsModalOpen(true);
	}, [preventive]);

	const parseApiError = useCallback((error: unknown): string => {
		const err = error as {
			response?: { data?: { error?: { code?: string } } };
		};
		const code = err?.response?.data?.error?.code;
		if (typeof code === "string") {
			return getPreventiveMaintenanceErrorMessage(code);
		}
		return "Não foi possível salvar a preventiva. Tente novamente.";
	}, []);

	const handleSave = useCallback(
		async (data: PreventiveMaintenanceRequest) => {
			setAlert(null);
			setFieldErrors({});

			const requiredErrors = validateRequiredFields(data);
			const dateErrors = validatePreventiveDates(data);
			const newFieldErrors: PreventiveFieldErrors = {
				...requiredErrors,
				...dateErrors,
			};
			if (Object.keys(newFieldErrors).length > 0) {
				setFieldErrors(newFieldErrors);
				setAlert({
					type: "error",
					title: "Campos inválidos",
					description: "Verifique os dados e tente novamente.",
				});
				return;
			}

			try {
				if (modalMode === "add") {
					await createMutation.mutateAsync({ vehicleId, data });
					toast.success("Preventiva adicionada com sucesso");
				} else if (preventive?.id) {
					await updateMutation.mutateAsync({
						preventiveId: preventive.id,
						vehicleId,
						data,
					});
					toast.success("Preventiva atualizada com sucesso");
				}
				setIsModalOpen(false);
			} catch (e) {
				const apiError = getApiErrorFromException(e);
				if (apiError?.fieldsErrors) {
					setFieldErrors(mapApiFieldErrorsToForm(apiError.fieldsErrors));
				}
				const message =
					apiError?.errors?.[0] ?? apiError?.detail ?? parseApiError(e);
				setAlert({ type: "error", title: "Erro", description: message });
			}
		},
		[
			modalMode,
			vehicleId,
			preventive,
			createMutation,
			updateMutation,
			parseApiError,
		]
	);

	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false);
		setAlert(null);
		setFieldErrors({});
	}, []);

	const handleFormChange = useCallback(
		(field: keyof PreventiveMaintenanceFormData, value: string | number) => {
			setFormData((prev) => ({ ...prev, [field]: value }));
			setFieldErrors((prev) => {
				const next = { ...prev, [field]: undefined };
				// Valida data da última preventiva ao sair do campo: não pode ser futura
				if (field === "dateOfLastPreventive" && typeof value === "string") {
					const msg = validateLastDateNotFuture(value);
					if (msg) {
						next.dateOfLastPreventive = msg;
					}
				}
				return next;
			});
		},
		[]
	);

	const isSaving = createMutation.isPending || updateMutation.isPending;
	const hasData = !!preventive;

	function renderCardContent() {
		if (isLoading) {
			return (
				<div className="flex min-h-[120px] items-center justify-center text-gray-500 text-sm">
					Carregando...
				</div>
			);
		}
		if (hasData && preventive) {
			return <PreventiveCardContent preventive={preventive} />;
		}
		return (
			<EmptyState className="min-h-40 w-full py-6" size="md">
				<EmptyState.Header pattern="none">
					<EmptyState.FeaturedIcon
						className="text-brand-500"
						color="brand"
						icon={Tool02}
						theme="modern"
					/>
				</EmptyState.Header>
				<EmptyState.Content>
					<EmptyState.Title>Nenhuma preventiva cadastrada</EmptyState.Title>
					<EmptyState.Description>
						Clique em Adicionar para registrar.
					</EmptyState.Description>
				</EmptyState.Content>
			</EmptyState>
		);
	}

	return (
		<>
			<div className="z-0 flex flex-col gap-3 rounded-[20px] border border-secondary_alt border-solid bg-bg-secondary p-6">
				<div className="flex flex-nowrap items-center justify-between gap-2">
					<div>
						<h3 className="font-medium text-gray-900 text-sm">
							Manutenções preventivas
						</h3>
						<p className="text-gray-600 text-xs">
							Última e próxima manutenção preventiva do veículo
						</p>
					</div>
					<div className="flex items-center gap-2">
						{hasData ? (
							<Button color="link-color" onClick={openEditModal} size="sm">
								Editar
							</Button>
						) : (
							<Button color="link-color" onClick={openAddModal} size="sm">
								Adicionar
							</Button>
						)}
					</div>
				</div>

				{renderCardContent()}
			</div>

			<PreventiveMaintenanceModal
				alert={alert}
				fieldErrors={fieldErrors}
				formData={formData}
				isOpen={isModalOpen}
				isSaving={isSaving}
				mode={modalMode}
				onAlertClose={() => setAlert(null)}
				onChange={handleFormChange}
				onClose={handleCloseModal}
				onSave={handleSave}
			/>
		</>
	);
};

function PreventiveCardContent({
	preventive,
}: {
	preventive: PreventiveMaintenance;
}) {
	return (
		<div className="flex flex-wrap gap-4">
			<div className="flex flex-col gap-0.5">
				<p className="font-normal text-gray-700 text-sm leading-5">
					Data da última preventiva
				</p>
				<p className="font-bold text-base text-gray-900 leading-6">
					{preventive.dateOfLastPreventive
						? formatDateToDdMmYyyy(preventive.dateOfLastPreventive)
						: "—"}
				</p>
			</div>
			<div className="flex flex-col gap-0.5">
				<p className="font-normal text-gray-700 text-sm leading-5">
					Quilometragem da última
				</p>
				<p className="font-bold text-base text-gray-900 leading-6">
					{formatKm(preventive.kilometersOfLastPreventive)}
				</p>
			</div>
			<div className="flex flex-col gap-0.5">
				<p className="font-normal text-gray-700 text-sm leading-5">
					Data da próxima preventiva
				</p>
				<p className="font-bold text-base text-gray-900 leading-6">
					{preventive.dateOfNextPreventive
						? formatDateToDdMmYyyy(preventive.dateOfNextPreventive)
						: "—"}
				</p>
			</div>
		</div>
	);
}
